import {useState} from 'react'
import {useTranslation} from 'react-i18next'
import {Tag} from '@/components/ui/tag'
import {NativeSelect} from '@/components/native-select'
import {EvidenceListItem, UploadTrigger} from '@/components/upload-trigger'
import {rutaDeDescarga, type Evidence} from '@/hooks/sgsst/evidencias/use-evidence'
import {useSubirEvidenciaInvestigacion, type EvidenceKind} from '@/hooks/sgsst/incidentes/use-investigacion'

const KINDS: EvidenceKind[] = ['file', 'furat', 'acta_investigacion']

/** Evidencia de la investigación (Res. 1401/2007 art. 9). `investigacionId` es el id de la fila de
 * investigación, no del incidente: mientras no llegue se pasa `''` con `editable` apagado. */
export function InvestigacionEvidencia({
  businessId,
  incidenteId,
  investigacionId,
  items,
  editable,
}: {
  businessId: string
  incidenteId: string
  investigacionId: string
  items: Evidence[]
  editable: boolean
}) {
  const {t} = useTranslation()
  const [kind, setKind] = useState<EvidenceKind>('file')
  const {start, progress, error} = useSubirEvidenciaInvestigacion(businessId, incidenteId, investigacionId, kind)

  return (
    <div className="space-y-2" data-testid="investigacion-evidencia">
      {editable && (
        <div className="flex flex-wrap items-center gap-2">
          <NativeSelect
            value={kind}
            data-testid="evidencia-kind"
            onChange={(e) => setKind(e.target.value as EvidenceKind)}
            className="w-auto px-2 text-xs">
            {KINDS.map((k) => (
              <option key={k} value={k}>
                {t(`investigacion.evidencia.kind.${k}`)}
              </option>
            ))}
          </NativeSelect>
          <UploadTrigger
            label={t('investigacion.evidencia.adjuntar')}
            progress={progress}
            testId="evidencia"
            onPick={start}
          />
        </div>
      )}
      {error && (
        <p className="text-sm text-destructive" data-testid="evidencia-error">
          {error}
        </p>
      )}
      {items.length === 0 ? (
        <p className="text-xs text-muted-foreground">{t('investigacion.evidencia.vacio')}</p>
      ) : (
        <ul className="space-y-1.5" data-testid="evidencia-list">
          {items.map((item) => (
            <EvidenceListItem
              key={item.id}
              item={item}
              ruta={rutaDeDescarga(businessId, item.id)}
              testId="evidencia-item"
              trailing={<Tag tone="neutral">{t(`investigacion.evidencia.kind.${item.kind}`)}</Tag>}
            />
          ))}
        </ul>
      )}
    </div>
  )
}
